import queryString from 'query-string';
import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { BASE_URL } from 'shared/constants/AppConst';
import AppContextPropsType from 'types/AppContextPropsType';

import purchaseAPI from '@healthme/services/api/purchases/Index';
import cartService from '@healthme/services/cart/index';
import patientService from '@healthme/services/patient/index';
import AppContext from '@healthme/utility/AppContext';

import { cacheApi2 } from '../../../utility/Utils';

export default function Initializer() {
	const pathArr = window.location.pathname
		.replace('/v1', '')
		.split('/')
		.filter(e => e.length);
	const orgSlug = pathArr[0].toLowerCase();
	const marketplaceSlug = pathArr[1]?.toLowerCase();
	const history = useHistory();

	const location = useLocation();
	const qs = queryString.parse(location.search);
	const { updatePrimaryColor, updateImageLogo, setPageLoading } =
		useContext<AppContextPropsType>(AppContext);
	React.useEffect(() => {
		setPageLoading(true);
	}, []);

	useQuery(
		`organization:${orgSlug}`,
		() => {
			if (orgSlug !== 'paylink') {
				return cacheApi2(`public/organizations/${orgSlug}`, {}, orgSlug);
			}
			return Promise.resolve(null);
		},
		{
			onSuccess: data => {
				console.log('we should not be here');
				patientService.setPatientOrganization(data);
				localStorage.setItem('orgSlug', orgSlug);
				if (!marketplaceSlug || marketplaceSlug === 'paylink') {
					sessionStorage.clear();

					let queryParams = {
						organization_id: data.id,
					};

					cacheApi2(
						`public/marketplaces`,
						{ queryParams },
						`initializer-${data.id}`
					).then(resp => {
						if (resp.data.length) {
							if (marketplaceSlug === 'paylink') {
								// HEA-366: Capture the legacy "first marketplace in the
								// org" as a fallback. We'll prefer the marketplace
								// embedded on the token (set from the org's configured
								// default) once we have it.
								const fallbackMarketplace = resp.data[0];
								const qs = queryString.parse(location.search);
								const paylink = qs?.t;
								purchaseAPI
									.getRaw(`paylinks/token`, {
										queryParams: { t: paylink },
									})
									.then(tokenResp => {
										console.log('paylink', paylink);
										console.log('token response', tokenResp);
										// Use the token's marketplace when available;
										// fall back to the org's first marketplace only
										// if the token has none (legacy tokens minted
										// before the BE preload existed).
										const marketplace =
											typeof tokenResp !== 'string' &&
											tokenResp?.marketplace?.name_slug
												? tokenResp.marketplace
												: fallbackMarketplace;
										if (typeof tokenResp === 'string') {
											setPageLoading(false);
											window.location.href = `/v1/${orgSlug}/${marketplace.name_slug}/expired`;
											return;
										}
										localStorage.setItem(
											'paylinkPayload',
											JSON.stringify(tokenResp)
										);
										localStorage.setItem(
											'marketplaceSlug',
											marketplace.name_slug
										);
										cartService.clear();
										window.location.href = `/v1/index.html?redirectUrl=${encodeURIComponent(
											`${orgSlug}/${marketplace.name_slug}/checkout`
										)}&t=${paylink}`;
									})
									.catch(e => {
										window.location.href = `/v1/index.html?redirectUrl=${encodeURIComponent(
											`${orgSlug}/404`
										)}`;
									});
								return;
							}
						}
						console.log('resp.data[0].name_slug', resp.data[0].name_slug);
						window.location.href = `/v1/index.html?redirectUrl=${encodeURIComponent(
							`${orgSlug}/${resp.data[0].name_slug}`
						)}`;
						return;
					});
				}
				if (marketplaceSlug) {
					let key = `initializer-${marketplaceSlug}`;
					cacheApi2(`public/marketplaces/${marketplaceSlug}`, {}, key).then(
						marketplace => {
							if (typeof marketplace === 'string' || marketplace?.error) {
								return;
							}
							if (updatePrimaryColor)
								updatePrimaryColor(
									'#' + marketplace?.primary_color.replace('#', '')
								);
							if (updateImageLogo && marketplace?.image_path)
								updateImageLogo(`${BASE_URL}/${marketplace?.image_path}`);
							patientService.set(
								'marketplaces',
								JSON.stringify(marketplace),
								'marketplace'
							);
							localStorage.setItem('marketplaceSlug', marketplaceSlug);
							patientService.setPatientMarketplace(marketplace);
							setPageLoading(false);
						}
					);
				}
			},
		}
	);
	// Handle paylink URL processing
	React.useEffect(() => {
		const handlePaylink = async () => {
			if (window.location.pathname.includes('/paylink') && qs.org) {
				try {
					localStorage.setItem('paylink', qs.token);
					localStorage.setItem('orgSlug', qs.org);
					const paylinkData = await purchaseAPI.getRaw(`paylinks/token`, {
						queryParams: { t: qs.token },
					});
					console.log('paylink data is string', typeof paylinkData === 'string');
					if (typeof paylinkData === 'string') {
						const orgResponse = await purchaseAPI.getRaw(
							`public/organizations/${qs.org}`
						);
						const marketplaceResp = await purchaseAPI.getRaw(`public/marketplaces`, {
							queryParams: {
								organization_id: orgResponse.id,
							},
						});
						const marketplace = marketplaceResp.data[0];
						console.log('paylink data', paylinkData);
						window.location.href = `/v1/${qs.org}/${marketplace.name_slug}/expired`;
						return;
					}
					// HEA-366: Prefer the marketplace the backend embedded on the token
					// (post-HEA-366 BE preloads it from the token's `marketplace_id`,
					// which was set at creation time from `org.default_paylink_marketplace_id`).
					// The legacy `data[0]` lookup just grabbed whichever marketplace the
					// org-scoped list returned first — wrong for orgs with multiple
					// marketplaces (e.g. Boston Hernia → Boston Gallbladder + Boston
					// Hernia). Fall back to the old behavior only if the token has no
					// embedded marketplace, so very old tokens still in their TTL
					// window behave exactly like today.
					let marketplace = paylinkData.marketplace;
					if (!marketplace?.name_slug) {
						const marketplaceResp = await purchaseAPI.getRaw(
							`public/marketplaces`,
							{
								queryParams: {
									organization_id: paylinkData.organization_id,
								},
							}
						);
						marketplace = marketplaceResp.data[0];
					}
					localStorage.setItem('paylinkPayload', JSON.stringify(paylinkData));
					localStorage.setItem('marketplaceSlug', marketplace.name_slug);
					history.push(
						`/${paylinkData.organization.name_slug}/${marketplace.name_slug}/checkout?t=${qs.token}`
					);
				} catch (error) {
					console.error('Error processing paylink:', error);
				}
			}
		};

		handlePaylink();
	}, [qs.org, qs.token, history]);

	return <></>;
}
